<template>
  <div class="size-selector">
    <div v-if="loaded">
      <!-- When there are multiple options for a product i.e. waist AND length -->
      <div
        v-if="hasMultipleOptions"
        class="size-selector__wrapper"
      >
        <!-- Primary Option -->
        <div class="size-selector__set">
          <p
            class="size-selector__title text-body-2xs"
            v-text="primaryOption"
          />

          <div class="size-selector__content">
            <div
              v-for="(option, index) in variantOptions[primaryOption]"
              :key="index"
              class="size-selector__tile text-body-2xs"
              :class="{
                [$class('active')] : option.size === relatedOption?.size,
                [$class('disabled')] : !option?.relatedOptions.length
              }"
              role="button"
              @click="setRelatedOption(option, true)"
              v-text="option.size"
            />
          </div>
        </div>

        <!-- Secondary Option -->

        <transition-height>
          <div
            v-if="relatedOption"
            class="size-selector__set"
          >
            <p
              class="size-selector__title text-body-2xs"
              v-text="relatedOption.relatedOptionTitle"
            />

            <div class="size-selector__content">
              <div
                v-for="(option, index) in relatedOption.relatedOptions"
                :key="index"
                class="size-selector__tile text-body-2xs"
                :class="{
                  [$class('disabled')] : !option?.available,
                  [$class('active')] : option.id === secondOption?.id
                }"
                role="button"
                :aria-label="`${option.displayOption} ${$string('accessibility.select_button')}`"
                @click="updateProductVariant(option)"
                v-text="option.displayOption"
              />
            </div>
          </div>
        </transition-height>
      </div>

      <!-- When there is only one option i.e. size -->
      <div
        v-else
        class="size-selector__wrapper"
        :class="{ 'size-selector__wrapper--single-size': hasSingleSize }"
      >
        <div class="size-selector__set">
          <div
            v-if="sortedVariants.length > 0"
            class="size-selector__content">
            <div
              v-for="(variant, index) in sortedVariants"
              :key="index"
              class="size-selector__tile text-body-2xs"
              :class="{
                [$class('disabled')] : !variant?.available,
                [$class('active')] : variant.id === secondOption?.id
              }"
              @click="updateProductVariant(variant)"
              v-text="variant.selectedOptions[0].value"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      :class="$class('loading')"
    >
    LOADING
      <!-- Show loading content during vue loading -->
      <div class="size-selector__wrapper">
        <div
          v-for="(set, index) in productOptions"
          :key="index"
          class="size-selector__set"
        >
          <p
            v-if="productOptions?.value > 1"
            class="size-selector__title text-body-2xs"
            v-text="set.name"
          />

          <div class="size-selector__content">
            <div
              v-for="i in set.value"
              :key="i"
              class="size-selector__tile text-body-2xs"
              :style="{ animationDelay: `${0.1 * index}s` }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Size selector (size-selector)
 * -----------------------------------------------------------------------------
 * Displays available sizes for product variants.
 *
 * @param {Boolean|Number} currentVariant - Variant object.
 * @param {String} primaryVector - The first size category to display.
 * @param {Boolean, Object} product - The current product in view.
 * @param {Array} productOptions - Array of product option objects.
 * @param {Array} variants - Array of variants for the current product.
 *
 */
import { mapState } from 'vuex'

import TransitionHeight from '~global/core/transition-height'

export default {
  name: 'SizeSelector',

  components: {
    TransitionHeight,
  },

  props: {
    currentVariant: {
      type: [Number, Boolean],
      default: false,
    },
    primaryVector: {
      type: String,
      default: '',
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    productOptions: {
      type: Array,
      default: () => [],
    },
    variants: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['product-variant-update'],

  data() {
    return {
      loaded: false,
      primaryOption: null,
      relatedOption: null,
      secondOption: null,
      variantOptions: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      variant: (state) => state.product.variant,
    }),

    /**
     * Sorts the variants by ascending size value.
     * @returns {Array}
     */
    sortedVariants() {
      return [...this.variants].sort(
        (a, b) => Number(a.selectedOptions[0].value) - Number(b.selectedOptions[0].value),
      )
    },

    /**
     * Indicates if a PDP shows multiple selection vectors.
     * @returns {Boolean}
     */
    hasMultipleOptions() {
      return !(!this.product || this.product?.options?.length <= 1)
    },

    /**
     * Checks if there's only one size available for single option products.
     * @returns {Boolean}
     */
    hasSingleSize() {
      return !this.hasMultipleOptions && this.sortedVariants.length === 1
    },
  },

  watch: {

    /**
     * When the product changes, rebuild the variant options object.
     */
    product: {
      eager: true,
      handler: function() {
        this.createVariantOptions()
      },
    },

    variant(){
      if(!this.variant){
        this.secondOption = null
        return
      }

      this.secondOption = this.variant
    },

    variantOptions:{
      handler(){
        if(!this.variant){
          return
        }

        const primary = this.variantOptions[this.primaryOption]
        const variantPrimary =
          this.variant.selectedOptions.find(
            option => option.name === this.primaryOption,
          )

        if(!primary || !variantPrimary){
          return
        }

        const relatedOption = primary.find(option => option.size === variantPrimary.value)

        if(!relatedOption){
          return
        }
        this.setRelatedOption(relatedOption)
      },
    },
  },

  mounted() {
    this.createVariantOptions()
  },

  methods: {

    /**
     * Sets a primary selection vector if none is passed as a prop.
     */
    setPrimaryOption() {
      const options = Object.keys(this.variantOptions)

      if (!options?.length) {

        if (!this.product?.options?.length) {
          return
        }

        const primaryOption = this.product?.options?.[0]?.name

        if (!primaryOption) {
          return
        }

        this.primaryOption = primaryOption
        return
      }

      this.primaryOption = this.primaryVector ? this.primaryVector : options[0]
    },

    /**
     * Handles and builds the variantOptions object.
     */
    createVariantOptions() {
      if (!this.hasMultipleOptions) {
        this.variantOptions = {}
        this.setPrimaryOption()
        this.loaded = true

        // Auto-select the variant if there's only one size
        if (this.hasSingleSize && this.sortedVariants.length > 0) {
          this.$nextTick(() => {
            this.updateProductVariant(this.sortedVariants[0], true)
          })
        }

        return
      }

      if (!this.variants.length) {
        this.variantOptions = {}
        this.setPrimaryOption()
        this.loaded = true
        return
      }

      const variants = this.variants.reduce((agg, item) => {
        item.selectedOptions.forEach(option => {
          if (!agg[option.name]) {
            agg[option.name] = [
              {
                size: option.value,
                relatedOptions: [...this.getRelatedProducts(option.name, option.value)],
                relatedOptionTitle: this.getRelatedOptionTitle(option.name),
                primaryOptionTitle: option.name,
              },
            ]
          } else {
            if (!agg[option.name].find(param => param.size === option.value)) {
              agg[option.name].push(
                {
                  size: option.value,
                  relatedOptions: [...this.getRelatedProducts(option.name, option.value)],
                  relatedOptionTitle: this.getRelatedOptionTitle(option.name),
                  primaryOptionTitle: option.name,
                },
              )
            }
          }

          return agg
        })

        return agg
      }, {})

      /**
       * Ensure numerical sizes are sorted into ascending order only if its a number
       */
      Object.values(variants).forEach(variant => {
        if (typeof(Number(variant.size)) === 'number') {
          variant.sort((a, b) => Number(a.size) - Number(b.size))
        }
      })

      this.variantOptions = variants
      this.setPrimaryOption()
      this.loaded = true
    },

    /**
     * Saves a new selected variant and emits the result also.
     * @param {Object} val - Selected variant object.
     * @param {Boolean} singleSizeFirstLoad - Whether to load the single size first.
     */
    updateProductVariant(val, singleSizeFirstLoad = false) {
      this.secondOption = val
      this.$emit('product-variant-update', val, singleSizeFirstLoad)
    },

    /**
     * Sets the related option in response to an emitted event.
     * @param {Object} val - Selected variant object.
     */
    setRelatedOption(val, interactionTrigger = false) {
      if (interactionTrigger){
        this.secondOption = null
        this.$emit('product-variant-update', false)
      }
      this.relatedOption = val
    },

    /**
     * Gets the name to display with the secondary selection vector.
     * @param {String} name - Name associated with the secondary selection vector.
     */
    getRelatedOptionTitle(name) {
      const option = this.product?.options?.find(option => option.name !== name)
      return option?.name || false
    },

    /**
     * Builds an array of product objects based on the related vector.
     * @param {String} name - Name of the current selection vector.
     * @param {Object} value - Product variant object.
     */
    getRelatedProducts(name, value) {
      const products = this.variants.filter(product => {
        const match = product.selectedOptions.filter(
          option => option.name === name && option.value === value)
        if (match.length) {
          return product
        }
      })

      const relatedProducts = JSON.parse(JSON.stringify(products))

      relatedProducts.forEach(product => {
        const related = product.selectedOptions.find(option => option.name !== name)
        product.displayOption = related.value
      })

      return relatedProducts
    },
  },
}
</script>

<style lang="scss">
@import './size-selector';
</style>
